import React from 'react'
import { Typography, Box, IconButton, Modal, ModalProps } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Close } from '@mui/icons-material'
import { MediaSchema as MediaSchemaType } from '../../types/media'
import ReactPlayer from 'react-player/lazy'
import MediaSEO from '../SEO/MediaSEO'

interface Props extends Omit<ModalProps, 'children'> {
  asset: string
  mediaSchema: MediaSchemaType
  onClose?: () => void
}

const MediaModal: React.FC<Props> = (props) => {
  const { classes, cx } = useStyles()
  const isContentfulFile = props.asset.includes('ctfassets.net')
  const isFacebook = props.asset.includes('facebook.com')

  return (
    <Modal
      {...props}
      className={classes.modal}
      BackdropProps={{ className: classes.backdrop }}
    >
      <Box
        className={cx({
          [classes.box]: true,
        })}
      >
        <IconButton
          aria-label="close"
          className={classes.iconButton}
          onClick={props?.onClose}
          size="large"
        >
          <Close />
        </IconButton>
        <MediaSEO mediaSchema={props.mediaSchema} />
        <Box
          className={cx({
            [classes.boxMedia]: true,
          })}
        >
          <Box
            className={cx({
              [classes.boxStreaming]: !isContentfulFile,
            })}
          >
            <ReactPlayer
              className={cx({
                [classes.player]: true,
                [classes.playerStreaming]: !isContentfulFile,
              })}
              controls={true}
              height="100%"
              playing={!isFacebook}
              url={props.asset}
              width="100%"
            />
          </Box>
          {props.mediaSchema.transcript && (
            <Box>
              <Typography className={classes.h3} variant="h3">
                Transcript
              </Typography>
              <Typography className={classes.transcript}>
                {props.mediaSchema.transcript}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  )
}

const useStyles = makeStyles()((theme) => ({
  box: {
    alignItems: 'flex-end',
    display: 'flex',
    flexDirection: 'column',
    height: 'auto',
    maxHeight: '90%',
    maxWidth: '71rem',
    width: '90%',

    '&:focus': {
      outline: 'none',
    },
  },
  boxMedia: {
    backgroundColor: theme.palette.background.default,
    height: 'auto',
    maxHeight: '90%',
    overflow: 'scroll',
    padding: '2.5rem 2.5rem 3rem',
    position: 'relative',
    width: '100%',

    [theme.breakpoints.down('md')]: {
      padding: '1rem 1rem 2rem',
    },
  },
  boxStreaming: {
    maxHeight: '56.25%',
    paddingTop: '56.25%',
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
  },
  h3: {
    marginTop: '2.5rem',
  },
  iconButton: {
    color: theme.colors.gray['a200'],
    marginBottom: '1.5rem',
    padding: 0,
    borderRadius: 0,

    '&:active': {
      color: theme.palette.background.default,
    },
    '&:hover': {
      color: theme.palette.background.default,
    },
  },
  modal: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  player: {
    '& video': {
      '&:focus': {
        outline: 'none',
      },
    },
  },
  playerStreaming: {
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  transcript: {
    marginTop: '.5rem',
    whiteSpace: 'pre-wrap',
  },
  backdrop: {
    backgroundColor: `${theme.colors.blue[600]} !important`, // Mui forces you to use !important to override their inline styles
  },
}))

export default MediaModal
